import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CssBaseline, Box } from '@material-ui/core';
import { Formik, Form, Field, useFormikContext } from 'formik';
import { Calendar } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { getAuth } from 'firebase/auth';
import { app } from '../../firebase/firebase-config';
import './BookingPage.css';

const auth = getAuth(app);

const DefaultDayPicker = ({ onDateSelected }) => {
    const [date, setDate] = useState(new Date());

    function handleDateSelected(dateSelected) {
        setDate(dateSelected);
        onDateSelected(dateSelected);
    }

    return (
        <Box style={{ width: '100%' }}>
            <Calendar
                className="calendar"
                date={date}
                onChange={(date) => handleDateSelected(date)}
                minDate={new Date()}
                maxDate={new Date(new Date().setMonth(new Date().getMonth() + 3))}
            />
        </Box>
    );
};

const ServiceField = ({ serviceOptions, setSelectedService }) => {
    const { setFieldValue } = useFormikContext();

    const handleServiceChange = (event) => {
        const service = event.target.value;
        setFieldValue('service', service);
        setSelectedService(service);
    };

    return (
        <Field name="service" as="select" id="service" onChange={handleServiceChange}>
            <option value="" label="Select service" />
            {serviceOptions.map((service, index) => (
                <option key={index} value={service}>{service}</option>
            ))}
        </Field>
    );
};

const BookingForm = ({ healer, selectedDate, uid, onDateSelected, onClose }) => {
    const serviceOptions = healer.services ? healer.services.split(',') : [];
    var priceOptions = [];

    if (healer.servicePrices == null) {
        alert("This user does not have price. Please check again");
        window.location.assign("/home");
    } else {
        if (healer.servicePrices.indexOf(",") > -1) {   //multiple prices
            const priceOptionsArray = healer.servicePrices.split(',');
            priceOptionsArray.map(price => {
                // Store the original price in dollars, don't multiply by 100
                priceOptions.push(Number(price).toString());
            })
        } else {  //single price
            // Store the original price in dollars, don't multiply by 100
            priceOptions.push(Number(healer.servicePrices).toString());
        }
    }

    const [timeSlots, setTimeSlots] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedService, setSelectedService] = useState('');
    const [isLoadingSlots, setIsLoadingSlots] = useState(false);

    // Function to get the cost for the selected service
    const getServiceCost = (serviceName) => {
        if (!serviceName) return null;

        const serviceIndex = serviceOptions.findIndex(service => service === serviceName);
        if (serviceIndex !== -1 && priceOptions[serviceIndex]) {
            // Price is already in dollars, just format it
            const price = Number(priceOptions[serviceIndex]);
            return price.toFixed(2);
        }
        return null;
    };

    // Helper function to convert time format (HH:mm to 12-hour format)
    const formatTimeForDisplay = (time24) => {
        if (!time24) return '';

        const [hours, minutes] = time24.split(':').map(Number);
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    };

    // Helper function to convert 12-hour format back to 24-hour format
    const formatTimeForAPI = (time12) => {
        if (!time12) return '';

        // Parse 12-hour format (e.g., "10:30 AM")
        const match = time12.match(/(\d+):(\d+)\s*(AM|PM)/i);
        if (!match) return time12;

        let [_, hours, minutes, period] = match;
        hours = parseInt(hours);
        minutes = parseInt(minutes);

        if (period.toUpperCase() === 'PM' && hours !== 12) {
            hours += 12;
        } else if (period.toUpperCase() === 'AM' && hours === 12) {
            hours = 0;
        }

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        if (selectedDate && selectedService) {
            setIsLoadingSlots(true);
            const formattedDate = selectedDate.toISOString().split('T')[0];
            console.log(`Fetching availability for healer ID: ${healer.id} on date: ${formattedDate} service: ${selectedService}`);

            // Find the service ID for the selected service using serviceIds from healer data
            const serviceNames = healer.services ? healer.services.split(',') : [];
            const serviceIds = healer.serviceIds ? healer.serviceIds.split(',') : [];

            console.log('Service Names:', serviceNames);
            console.log('Service IDs:', serviceIds);
            console.log('Selected Service:', selectedService);

            const serviceIndex = serviceNames.findIndex(name => name.trim() === selectedService.trim());
            const serviceId = serviceIndex !== -1 ? serviceIds[serviceIndex] : null;

            console.log('Service Index:', serviceIndex);
            console.log('Service ID found:', serviceId);

            if (!serviceId) {
                console.error('Service ID not found for:', selectedService);
                setTimeSlots([]);
                setIsLoadingSlots(false);
                return;
            }

            // Try the correct availability endpoint
            const availabilityUrl = `${process.env.REACT_APP_API_BASE_URL}/availability?user_id=${healer.id}&date=${formattedDate}&service_id=${serviceId}`;
            console.log('Fetching from URL:', availabilityUrl);

            axios.get(availabilityUrl)
                .then(response => {
                    console.log('API Response:', response.data);
                    console.log('Response length:', response.data.length);
                    const availabilities = response.data;

                    // Convert availability slots to time slots for display
                    const availableTimeSlots = availabilities.map(slot => ({
                        start: formatTimeForDisplay(slot.start_time),
                        end: formatTimeForDisplay(slot.end_time),
                        originalStart: slot.start_time,
                        originalEnd: slot.end_time,
                        id: slot.id
                    }));

                    console.log('Available time slots:', availableTimeSlots);
                    console.log('Time slots length:', availableTimeSlots.length);
                    setTimeSlots(availableTimeSlots);
                })
                .catch(error => {
                    console.error("There was an error fetching the time slots!", error);
                    console.error("Error response:", error.response?.data);
                    console.error("Error status:", error.response?.status);
                    setTimeSlots([]);
                })
                .finally(() => {
                    setIsLoadingSlots(false);
                });
        } else {
            setTimeSlots([]);
        }
    }, [selectedDate, selectedService, healer.id, healer.services, healer.serviceIds]);

    return (
        <Formik
            initialValues={{
                service: "",
                time: ""
            }}
            validate={(values) => {
                const errors = {};
                if (!values.service) {
                    errors.service = "Service is required";
                }
                if (!values.time) {
                    errors.time = "Time is required";
                }
                return errors;
            }}
            onSubmit={async (values, actions) => {
                if (!selectedDate) {
                    console.error("⛔ You must select a date before submitting.");
                    setErrorMessage("Please select a date before submitting.");
                    actions.setSubmitting(false);
                    return;
                }
                console.log("Selected date before submission:", selectedDate);
                console.log("Form submitted with values:", values);

                var counter = -1;
                for (const serviceItem of serviceOptions) {
                    counter++;
                    if (serviceItem === values.service) {
                        break;
                    }
                }
                const paymentData = {
                    healer_name: healer.firstname + ' ' + healer.lastname,
                    healer_email: healer.email,
                    amount: priceOptions[counter],
                    currency: "cad",
                    items: [
                        {
                            service_name: values.service,
                            quantity: 1,
                            price: priceOptions[counter],
                        }
                    ]
                };

                const appointmentData = {
                    ...values,
                    uid,
                    healer: healer.id,
                    date: selectedDate.toISOString(),
                };

                localStorage.setItem('latestAppointment', JSON.stringify(appointmentData));
                localStorage.setItem('appointmentJustBooked', 'true');

                console.log("Sending appointmentData:", appointmentData);

                await axios.post(`${process.env.REACT_APP_API_BASE_URL}/appointments`, appointmentData)
                    .then(response => {
                        console.log('Appointment created:', response.data);
                        // Save flag for schedule page refresh to fetch new appointment
                        localStorage.setItem('appointmentJustBooked', 'true');
                        actions.setSubmitting(false);
                        setErrorMessage('');
                    })
                    .catch(error => {
                        console.error("There was an error making the appointment!", error);
                        actions.setSubmitting(false);
                        if (error.response && error.response.data && error.response.data.message) {
                            setErrorMessage(error.response.data.message);
                        } else {
                            setErrorMessage('There was an error making the appointment.');
                        }
                    });

                console.log("Sending paymentData:", paymentData);

                await axios.post(`${process.env.REACT_APP_API_BASE_URL}/payment`, paymentData)
                    .then(response => {
                        actions.setSubmitting(false);
                        setErrorMessage('');
                        window.location.assign(response.data.url);
                    })
                    .catch(error => {
                        console.error("There was an error making the appointment payment!", error);
                        actions.setSubmitting(false);
                        setErrorMessage('There was an error making the appointment payment.');
                        window.location.assign('/search');
                    });
            }}
        >
            {({ handleSubmit, values, errors, touched }) => (
                <div className="bookingFormContainer">
                    <Form className="bookingForm" onSubmit={handleSubmit}>
                        <div className="bookingPageContainer">
                            <div className="bookingServiceContainer">
                                <p>Service:</p>
                                <ServiceField serviceOptions={serviceOptions} setSelectedService={setSelectedService} />
                                {values.service && getServiceCost(values.service) > 0 && (
                                    <div className="serviceCost">
                                        Cost: ${getServiceCost(values.service)}
                                    </div>
                                )}
                                {errors.service && touched.service && (
                                    <div className="error">{errors.service}</div>
                                )}
                            </div>
                            <div className="bookingTimeContainer">
                                <p>Time:</p>
                                <Field name="time" as="select" id="time">
                                    <option value="" label="Select time" />
                                    {isLoadingSlots ? (
                                        <option value="" disabled>Loading available slots...</option>
                                    ) : timeSlots.length > 0 ? (
                                        timeSlots.map((timeSlot, index) => (
                                            <option key={`slot-${index}`} value={`${timeSlot.originalStart}-${timeSlot.originalEnd}`}>
                                                {timeSlot.start} - {timeSlot.end}
                                            </option>
                                        ))
                                    ) : (
                                        <option value="" disabled>
                                            {selectedDate && selectedService ? 'No available slots for this date and service' : 'Please select a date and service first'}
                                        </option>
                                    )}
                                </Field>
                                {errors.time && touched.time && (
                                    <div className="error">{errors.time}</div>
                                )}
                                {selectedDate && selectedService && timeSlots.length === 0 && !isLoadingSlots && (
                                    <div className="no-slots-message">
                                        <p>No available slots found for {selectedDate.toLocaleDateString()} and {selectedService}. Please try a different date or service.</p>
                                    </div>
                                )}
                            </div>
                            <div className="bookingSubmitButton">
                                <button type="submit" className="btn" disabled={isLoadingSlots || timeSlots.length === 0}>
                                    {isLoadingSlots ? 'Loading...' : 'Proceed to Payment'}
                                </button>
                            </div>
                            {errorMessage && (
                                <div className="error">{errorMessage}</div>
                            )}
                        </div>
                    </Form>
                </div>
            )}
        </Formik>
    );
};

const BookingPage = () => {
    const { healerId } = useParams();
    const navigate = useNavigate();
    const [userState, setUserState] = useState(null);
    const [userDetails, setUserDetails] = useState(null);
    const [healer, setHealer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [avgRating, setAvgRating] = useState(0);

    useEffect(() => {
        auth.onAuthStateChanged((user) => {
            if (user) {
                setUserState(user);
                axios.get(`${process.env.REACT_APP_API_BASE_URL}/users`).then((response) => {
                    for (const i of response.data) {
                        if (i.email.toLowerCase() === user.email.toLowerCase()) {
                            setUserDetails(i);
                            console.log("Matched user details:", i);
                            break;
                        }
                    }
                });
            } else {
                console.error("Sorry. You need to sign in before booking");
                navigate('/signin');
            }
        });
    }, [navigate]);

    useEffect(() => {
        if (healerId) {
            // Fetch healer details
            axios.get(`${process.env.REACT_APP_API_BASE_URL}/users/healers`)
                .then(response => {
                    const healerData = response.data.find(h => h.id === parseInt(healerId));
                    if (healerData) {
                        setHealer(healerData);

                        // Fetch reviews for the healer
                        return axios.get(`${process.env.REACT_APP_API_BASE_URL}/review/${healerData.id}`);
                    } else {
                        console.error('Healer not found');
                        navigate('/search');
                        return Promise.reject('Healer not found');
                    }
                })
                .then(reviewsResponse => {
                    const reviewsData = reviewsResponse.data;
                    const totalRating = reviewsData.reduce((acc, review) => acc + review.rating, 0);
                    const averageRating = reviewsData.length > 0 ? totalRating / reviewsData.length : 0;

                    setReviews(reviewsData);
                    setAvgRating(averageRating);
                })
                .catch(error => {
                    console.error('Error fetching healer or reviews:', error);
                    if (error !== 'Healer not found') {
                        navigate('/search');
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [healerId, navigate]);

    if (loading) {
        return (
            <div className="bookingPage">
                <div className="loaderContainer">
                    <div className="loader"></div>
                </div>
            </div>
        );
    }

    if (!healer) {
        return (
            <div className="bookingPage">
                <div className="error">Healer not found</div>
            </div>
        );
    }

    const serviceOptions = healer.services ? healer.services.split(',') : [];

    return (
        <div className="bookingPage">
            <CssBaseline />
            <div className="bookingHeader">
                <button
                    className="backButton"
                    onClick={() => navigate('/search')}
                >
                    ← Back to Search
                </button>
            </div>

            <div className="bookingContent">
                {/* Left Column - Healer Info & Reviews */}
                <div className="healerInfoSection">
                    {/* Healer Profile */}
                    <div className="healerProfile">
                        <div className="healerHeader">
                            <div className="healerAvatar">
                                <div className="avatarPlaceholder">
                                    {healer.firstname?.charAt(0)}{healer.lastname?.charAt(0)}
                                </div>
                            </div>
                            <div className="healerInfo">
                                <h2>{healer.firstname} {healer.lastname}</h2>
                                <div className="healerRating">
                                    <div className="stars">
                                        {[...Array(5)].map((_, i) => (
                                            <span key={i} className={`star ${i < Math.round(avgRating) ? 'filled' : 'empty'}`}>
                                                ★
                                            </span>
                                        ))}
                                    </div>
                                    <span className="ratingText">
                                        {avgRating.toFixed(1)} ({reviews.length} reviews)
                                    </span>
                                </div>
                            </div>
                        </div>

                        {healer.description && (
                            <div className="healerBio">
                                <h3>About</h3>
                                <p>{healer.description}</p>
                            </div>
                        )}

                        <div className="healerServices">
                            <h3>Services</h3>
                            <div className="serviceTags">
                                {serviceOptions.map((service, index) => (
                                    <span key={index} className="serviceTag">{service.trim()}</span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Reviews Section */}
                    <div className="reviewsSection">
                        <h3>Reviews</h3>
                        {reviews.length > 0 ? (
                            <div className="reviewsList">
                                {reviews.slice(0, 5).map((review, index) => (
                                    <div key={review.rid || index} className="reviewItem">
                                        <div className="reviewHeader">
                                            <div className="reviewStars">
                                                {[...Array(5)].map((_, i) => (
                                                    <span key={i} className={`star ${i < review.rating ? 'filled' : 'empty'}`}>
                                                        ★
                                                    </span>
                                                ))}
                                            </div>
                                            <span className="reviewDate">
                                                {review.createdat ? new Date(review.createdat).toLocaleDateString() : 'Recent'}
                                            </span>
                                        </div>
                                        <div className="reviewText">
                                            {review.comment}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="noReviews">
                                <p>No reviews yet</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Column - Booking Form */}
                <div className="bookingFormSection">
                    <h3>Book Appointment</h3>
                    <div className="bookingCalendar">
                        <h4>Select Date</h4>
                        <DefaultDayPicker onDateSelected={setSelectedDate} />
                    </div>
                    <BookingForm
                        healer={healer}
                        selectedDate={selectedDate}
                        uid={userDetails?.id}
                        onDateSelected={setSelectedDate}
                    />
                </div>
            </div>
        </div>
    );
};

export default BookingPage; 